/**
 * Copy to clipboard button component
 * Provides user feedback when copying text to clipboard
 */

import { useState } from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

interface CopyButtonProps extends ButtonProps {
  text: string;
  label?: string;
  successMessage?: string;
  icon?: boolean;
}

export function CopyButton({
  text,
  label = 'Copy',
  successMessage = 'Copied!',
  icon = false,
  ...props
}: CopyButtonProps) {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      toast.success(successMessage);

      // Reset state after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      toast.error('Failed to copy');
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="gap-1"
      {...props}
    >
      {isCopied ? (
        <>
          <Check className="h-4 w-4" />
          {!icon && 'Copied'}
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          {!icon && label}
        </>
      )}
    </Button>
  );
}
